import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import classNames from "classnames";
import {ChevronDownIcon, ChevronRightIcon} from "@heroicons/react/solid";
import FormContext from "../form-context";
import Form from "../index";
import _get from "lodash/get";
import axios from "axios";
import {arrayMove} from "@dnd-kit/sortable";
import {SortableContainer, SortableElement, SortableHandle} from "react-sortable-hoc";
import MenuIcon from "@heroicons/react/solid/MenuIcon";
import {getSortOptions} from "../../../websites/src/blocks/products-list-block/sort";
import {getFieldsets} from "../../../websites/src/blocks/products-list-block/filters";
import Combobox from "./combobox";
import AnimateHeight from "react-animate-height";
import {Disclosure} from "@headlessui/react";

const SORTTYPES = ['number', 'currency', 'date', 'text'];

const DEFAULTSORT = {
    products: [
        "Recently Added:date:createdAt",
        "Price:currency:unitPrice",
    ],
    schema: [
        "Recently Added:date:createdAt",
        "Title:text:title",
        "Recently Updated:date:publishedAt",
        "Expires Soon:date:expiresOn",
    ]
}

/**
 * Helpers to keep arrays in form state while giving the Combobox a stable string value.
 */
const serializeSortVal = (v) => {
    if (v === 'custom') return 'custom';
    if (Array.isArray(v)) return JSON.stringify(v);
    if (v == null) return JSON.stringify([]);
    const s = String(v);
    if (s.includes(',')) return JSON.stringify(s.split(','));
    return JSON.stringify([s]);
};

const deserializeSortVal = (v) => {
    if (v === 'custom') return 'custom';
    try {
        const parsed = JSON.parse(v);
        return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
        const s = String(v);
        return s.includes(',') ? s.split(',') : [s];
    }
};

const getSortOptionsForSource = (source, fields = []) => {
    const defaultSort = DEFAULTSORT[source] || [];
    const sortFilterOptions = (fields || [])
        .filter(f => SORTTYPES.includes(f.type))
        .map(f => `${f.label}:${f.type}:${f.value}`);

    const recentlyAddedOption = defaultSort[0];
    const remainingOptions = [...defaultSort.slice(1), ...sortFilterOptions];

    remainingOptions.sort((a, b) => {
        const aLabel = a.split(':')[0].toLowerCase();
        const bLabel = b.split(':')[0].toLowerCase();
        return aLabel.localeCompare(bLabel);
    });

    const finalOptions = recentlyAddedOption
        ? [recentlyAddedOption, ...remainingOptions]
        : remainingOptions;

    const sortFieldMeta = finalOptions.reduce((result, option) => {
        const [, type, field] = option.split(':');
        if (field) result[field] = {field, type};
        return result;
    }, {});

    return getSortOptions(finalOptions).map(o => ({
        ...o,
        value: serializeSortVal(o.value),
        meta: sortFieldMeta[o.value?.[0]] || null
    }));
};

export const getUserDefinedFields = (userDefinedFields, fields = []) => {
    userDefinedFields.forEach((userDefinedField) => {
        if (userDefinedField.userDefinedFieldsets) getUserDefinedFields(userDefinedField.userDefinedFieldsets, fields)
        else if (userDefinedField.fields) getUserDefinedFields(userDefinedField.fields, fields)
        else {
            const exists = fields.find(({value}) => value === userDefinedField.id);
            if (!exists && userDefinedField.label) fields.push({
                value: userDefinedField.id,
                type: userDefinedField.type,
                label: userDefinedField.label
            })
        }
    })
    return fields
}

const cleanValues = (values) => Object.fromEntries(
    Object.entries(values).filter(([_, v]) =>
        !(v === 0 || v === '' || (Array.isArray(v) && v.length === 0))
    )
);

const DragHandle = SortableHandle(() => (
    <div className="flex items-center cursor-pointer">
        <MenuIcon className="h-5 w-5 text-panels-700" aria-hidden="true"/>
    </div>
));

const SortableItem = SortableElement(({item, index, visibleSize, itemPosition}) => {
    const isHidden = visibleSize !== undefined && itemPosition >= visibleSize;

    return (
        <li
            className={classNames(
                "list-none bg-white flex items-center justify-between rounded border border-gray-500 shadow-button py-1 px-1.5",
                index > 0 && "mt-2",
                isHidden && "opacity-75"
            )}
        >
            <DragHandle/>
            <span className="grow text-sm font-semibold text-panels-700 text-left overflow-hidden text-ellipsis mx-2">
      {item.label || item.name || `Item ${index + 1}`}
    </span>
        </li>
    )
});

const SortableList = SortableContainer(({items, visibleSize}) => (
    <ul className="sortable-list space-y-2">
        {items.map((item, idx) => (
            <SortableItem key={item.id} itemPosition={idx} index={idx} item={item} visibleSize={visibleSize}/>
        ))}
    </ul>
));

export default function DataFilter({
                                       id,
                                       name,
                                       label,
                                       className,
                                       prefix,
                                       source,
                                       pathToUuid,
                                       pathToUuidFallbacks = [],
                                       description,
                                       showSort = true,
                                       allowFilterEditor = true,
                                       onFieldChange = () => {},
                                       ...props
                                   }) {
    const context = useContext(FormContext);
    const displayLabel = label || "Filter";
    const uuid = useMemo(() => {
        return [pathToUuid, ...pathToUuidFallbacks]
            .map((path) => _get(context.values.values, path))
            .find(Boolean);
    }, [context.values.values, pathToUuid, pathToUuidFallbacks]);
    const value = _get(context.values, name)
    const baseUrl = `${location.protocol}//${context.websiteManagementContext?.channel?.website?.previewDomain}`;
    const [loading, setLoading] = useState(() => allowFilterEditor);
    const [error, setError] = useState(null);
    const [fieldsets, setFieldsets] = useState([]);
    const [filters, setFilters] = useState([]);
    const [count, setCount] = useState();
    const containerRef = useRef();
    const listRef = useRef();
    const [sortBy, setSortBy] = useState(serializeSortVal(value?.sortBy ?? ["createdAt", "desc"]));
    const [sortOptions, setSortOptions] = useState(() => showSort ? getSortOptionsForSource(source) : []);
    const [items, setItems] = useState([]);
    const visibleSize = _get(context, 'values.values.item.size') ?? undefined;
    const [activeFilters, setActiveFilters] = useState(() => _get(context.values, `${name}.filters`) ?? {});
    const sortComboboxOptions = useMemo(() => ([
        {label: "Custom Sort", value: "custom"},
        ...sortOptions.map(o => ({label: o.label, value: o.value}))
    ]), [sortOptions]);
    const sortComboboxKey = useMemo(() => sortComboboxOptions.map(option => option.value).join('|'), [sortComboboxOptions]);

    useEffect(() => {
        const externalFilters = _get(context.values, `${name}.filters`) ?? {};
        const isDifferent = JSON.stringify(externalFilters) !== JSON.stringify(activeFilters);
        if (isDifferent) {
            setActiveFilters(externalFilters);
        }
    }, [context.values?.[name]?.filters]);

    useEffect(() => {
        if (!allowFilterEditor) {
            setLoading(false);
        }
    }, [allowFilterEditor]);

    useEffect(() => {
        setSortBy(serializeSortVal(value?.sortBy ?? ["createdAt", "desc"]));
    }, [value?.sortBy]);

    useEffect(() => {
        setSortOptions(showSort ? getSortOptionsForSource(source) : []);
    }, [source, showSort]);

    // Fetch item sort if custom
    useEffect(() => {
        const cancelToken = axios.CancelToken.source();
        const formValues = context?.values?.values;

        if (!uuid || !formValues || !baseUrl || !source) return;

        const payload = {
            ...formValues,
            item: {
                ...formValues.item,
                size: 100,
            },
            productFilter: {
                ...formValues.productFilter,
                filters: {
                    ...activeFilters
                }
            }
        };

        axios
            .post(`/api/v2/website/${source}`, payload, {
                headers: {"X-Preview-Domain": baseUrl},
                cancelToken: cancelToken.token,
            })
            .then(({data}) => {
                setCount(data.count ?? 0);
                if (!showSort || sortBy !== 'custom') return;

                const children = data?.items || [];

                const mapped = children.map((node) => ({
                    id: source === 'schema' ? node?.uuid : node?._id,
                    label: source === 'schema'
                        ? _get(node, "label", "") || _get(node, "title", "")
                        : _get(node, "_source.title", ""),
                }));

                const savedOrder = value?.order ?? [];
                const savedIds = savedOrder.map((item) => item.id);
                const labelLookup = Object.fromEntries(mapped.map((m) => [m.id, m.label]));

                const existing = savedOrder
                    .filter((item) => labelLookup[item.id] !== undefined)
                    .map((item) => ({
                        ...item,
                        label: labelLookup[item.id],
                    }));

                const newItems = mapped.filter((item) => !savedIds.includes(item.id));
                const ordered = [...existing, ...newItems];

                setItems(ordered);
                onItemsChange(ordered);
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    console.error("Failed to fetch filter data:", err);
                }
            });

        return () => cancelToken.cancel();
    }, [uuid, value, sortBy, source, activeFilters, showSort]);

    const onItemsChange = (items) => {
        onFieldChange(null, `${name}.order`, items);
    };

    const fetchFilters = (cancelToken, filtersState) => {
        if (!allowFilterEditor) {
            setFilters([]);
            setFieldsets([]);
            setLoading(false);
            return;
        }

        if (!uuid || !source || !baseUrl) return;

        setLoading(true);
        setError(null);

        axios
            .post(`/api/v2/website/filter`, {source, uuid, filters: filtersState}, {
                headers: {"X-Preview-Domain": baseUrl},
                cancelToken: cancelToken.token,
            })
            .then(({data}) => {
                let fields = [];
                setFilters(data.filters);
                setFieldsets(getFieldsets(data.filters, [], true));
                if (source === 'products') {
                    fields = getUserDefinedFields(data.templates || []);
                } else if (source === 'schema') {
                    const schema = context?.websiteManagementContext?.schema?.children?.find(s => s.uuid === uuid);
                    fields = getUserDefinedFields(schema?.userDefinedFieldsets || []);
                }

                if (showSort) {
                    setSortOptions(getSortOptionsForSource(source, fields));
                } else {
                    setSortOptions([]);
                }
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    console.error("Failed to fetch filter data:", err);
                    if (showSort) setSortOptions(getSortOptionsForSource(source));
                    setError(err.message || "Failed to fetch filter data");
                }
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (filters) setFieldsets(getFieldsets(filters, [], true));
    }, [activeFilters]);

    useEffect(() => {
        if (!allowFilterEditor) return;
        const cancelToken = axios.CancelToken.source();
        fetchFilters(cancelToken, activeFilters);
        return () => cancelToken.cancel();
    }, [allowFilterEditor, uuid, source, baseUrl]);

    const openFilterEditor = () => {
        if (!allowFilterEditor) return;
        const initialFilters = {...activeFilters};

        context.setContext({
            showChildForm: true,
            childForm: (
                <Form
                    id="data_filter_editor"
                    layout="filter-panel"
                    className="h-full"
                    heading={displayLabel}
                    source={source}
                    baseUrl={baseUrl}
                    uuid={uuid}
                    fieldsets={fieldsets}
                    values={activeFilters}
                    parentContext={context}
                    actions={[
                        {label: "Cancel", back: true},
                        {label: "Done", primary: true, icon: "check"},
                    ]}
                    onSubmit={(e, values) => {
                        delete values.userDefinedFieldsets
                        const cleanedValues = cleanValues(values);
                        setActiveFilters(cleanedValues);
                        onFieldChange(e, `${name}.filters`, cleanedValues);
                        context.setContext({showChildForm: false});
                    }}
                    onActionClick={(e, action) => {
                        if (action.back) {
                            setActiveFilters(initialFilters);
                            context.setContext({showChildForm: false});
                        }
                    }}
                    onFieldChange={(e, fieldName, value, values) => {
                        const cleanedValues = cleanValues(values);
                        onFieldChange(e, `${name}.filters`, cleanedValues);
                    }}
                />
            ),
        });
    };

    return (
        <>
            {!loading && (allowFilterEditor ? filters && Object.keys(filters).length > 0 : !!uuid) &&
                <div className={classNames("relative", className)}>
                    <div className="rounded border border-gray-500 shadow-button px-3 pt-2 pb-1.5 grow">
                        <div className="flex mb-1 justify-between">
                            <label htmlFor={id || name} className="block text-sm font-semibold text-gray-700">
                                {displayLabel}
                            </label>
                        </div>
                        <div className="relative">
                            {allowFilterEditor ? (
                                <div
                                    className="absolute top-0 left-0 right-0 h-[40px] -mt-[33px] flex items-center justify-between cursor-pointer"
                                    onClick={openFilterEditor}>
                                    <span className="grow"/>
                                    <span
                                        className="ml-1.5 rounded bg-gray-200 px-1 py-0.5 text-xs font-semibold tabular-nums text-gray-600">
                                        {count}
                                    </span>
                                    <ChevronRightIcon className="w-5 h-5 shrink-0 text-gray-500"/>
                                </div>
                            ) : (
                                <div className="flex min-h-[7px] items-center justify-end">
                                    <span
                                        className="rounded bg-gray-200 px-1 py-0.5 text-xs font-semibold tabular-nums text-gray-600">
                                        {count}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    {description && (
                        <p className="mt-1 mb-0 text-xs font-medium text-gray-500 whitespace-normal">
                            {description}
                        </p>
                    )}
                    {loading && <p className="text-xs text-gray-400 mt-1">Loading filters...</p>}
                    {error && <p className="text-xs text-red-500 mt-1">Error: {error}</p>}
                </div>}

            {showSort && allowFilterEditor && (
                <>
                    <div className={classNames("grow control-select-item-action-style", className, "mb-4")}>
                        <div className="flex mb-1 justify-between">
                            <label
                                htmlFor={`${name}.sortBy`}
                                className="block text-sm font-semibold text-gray-700">
                                Sort By
                            </label>
                        </div>
                        <div className="w-full relative">
                            <Combobox
                                key={sortComboboxKey}
                                name={`${name}.sortBy`}
                                className="w-full"
                                value={sortBy}
                                onChange={(e, fieldName, newVal) => {
                                    setSortBy(newVal);
                                    const next = newVal === 'custom' ? 'custom' : deserializeSortVal(newVal);
                                    const nextMeta = newVal === 'custom'
                                        ? null
                                        : sortOptions.find((o) => o.value === newVal)?.meta || null;
                                    onFieldChange(e, fieldName, next);
                                    onFieldChange(e, `${name}.sortByMeta`, nextMeta);
                                }}
                                required={true}
                                canCreate={false}
                                multiple={false}
                                options={sortComboboxOptions}
                                labelFieldPath="label"
                                valueFieldPath="value"
                                placeholder="Select sort option"
                                fuzzySearch={true}
                            />
                        </div>
                    </div>
                    <div ref={containerRef} className={classNames("relative", className)}>
                        {sortBy === "custom" && items.length > 0 && (
                    <Disclosure
                        as="div"
                        className="w-full mt-2"
                        defaultOpen={false}>
                        {({open}) => (
                            <>
                                <Disclosure.Button
                                    as="div"
                                    className="w-full flex justify-between items-center group cursor-pointer mb-1">
                                    <div>
                                        <label
                                            className="block text-sm font-semibold text-gray-700 group-hover:text-link">
                                            Item Sort Order
                                        </label>
                                        <p className="text-sm text-gray-500">
                                            Drag and drop to reorder items
                                        </p>
                                    </div>
                                    <div className="group-hover:text-link">
                                    <span
                                        className={classNames("border-current", open ? "chevron--up" : "chevron--down")}>
                                       <ChevronDownIcon
                                           className={classNames(
                                               "h-6 w-6 transform transition-transform duration-200 ease-in-out",
                                               open && "rotate-180"
                                           )}
                                       />
                                    </span>
                                    </div>
                                </Disclosure.Button>
                                <AnimateHeight
                                    duration={250}
                                    height={open ? 'auto' : 0}
                                    animateOpacity={true}>
                                    <Disclosure.Panel static className="pt-4">
                                        <SortableList
                                            ref={listRef}
                                            items={items}
                                            useDragHandle
                                            visibleSize={visibleSize}
                                            helperClass="sort-helper"
                                            getContainer={() => {
                                                let parent = containerRef.current.parentElement;
                                                while (
                                                    parent &&
                                                    !(parent.tagName === "BODY" || parent.scrollHeight > parent.offsetHeight)
                                                    )
                                                    parent = parent.parentElement;
                                                return parent || document.body;
                                            }}
                                            onSortStart={() => {
                                                const helper = document.getElementsByClassName("sort-helper")[0];
                                                if (helper) helper.style.zIndex = 50000;
                                            }}
                                            onSortEnd={({oldIndex, newIndex}) => {
                                                const newItems = arrayMove(items, oldIndex, newIndex);
                                                setItems(newItems);
                                                onItemsChange(newItems);
                                            }}
                                        />
                                    </Disclosure.Panel>
                                </AnimateHeight>
                            </>
                        )}
                    </Disclosure>
                        )}
                    </div>
                </>
            )}
        </>
    );
}
